/**
 * Match - Instruct — mirror Access subform `frmMatchingPSMainSubForm`
 * (file frontend/plan/access_all/form_frmMatchingPSMainSubForm.txt).
 *
 * Layout chuẩn Access — 2 cột độc lập (P trái / S phải), mỗi cột tự chứa toolbar,
 * filter, table riêng. Không chia sẻ state hay button giữa 2 bên. Cross-side
 * action (Direct P↔S, Data Plan) đặt ở top bar trung lập.
 *
 *   ┌────────────────  Top bar (cross-side actions)  ────────────────┐
 *   │ Match - Instruct      [Direct P ↔ S]  [Data Plan]               │
 *   └─────────────────────────────────────────────────────────────────┘
 *   ┌─── P COLUMN (50%) ──────────────┐ ┌─── S COLUMN (50%) ──────────────┐
 *   │ Banner: Contract P · Matching    │ │ Banner: Contract S · Matching    │
 *   │ [P-Instruction] [Edit Purchase]  │ │ [S-Instruction] [Edit Sale]      │
 *   │ [Delete P-Plan]                  │ │ [Delete S-Plan]                  │
 *   │ Grade: [▼]                       │ │ Grade: [▼]                       │
 *   │ Latest Delivery: [from] → [to]   │ │ Shipment From: [from] → [to]     │
 *   │ ┌──────────────────────┐         │ │ ┌──────────────────────┐         │
 *   │ │ Table P (scroll x)   │         │ │ │ Table S (scroll x)   │         │
 *   │ │ ContractNo│Date│...  │         │ │ │ ContractNo│Date│...  │         │
 *   │ └──────────────────────┘         │ │ └──────────────────────┘         │
 *   └──────────────────────────────────┘ └──────────────────────────────────┘
 *
 * VBA event procedures dịch sang React:
 * - Form_Load:           initial date range (fiscal year Oct→Sep) + ChangeValue.
 * - category*_AfterUpdate / start_date*_AfterUpdate / end_date*_AfterUpdate
 *   → ChangeValue:       persist filter + Requery (placeholder, chưa có endpoint).
 * - btnDirect_Click:     mở frmMatchingPSMainSubFormDirect.
 * - btnPStock_Click:     mở frmMatchingPSMainSubFormStockP (= P-Instruction).
 * - btnSStock_Click:     mở frmMatchingPSMainSubFormStockS (= S-Instruction).
 * - btnList_Click:       mở frmMatchingPSMainSubFormDataPlan.
 * - btnDelete_Click:     soft-delete plan — split thành Delete-P-Plan / Delete-S-Plan
 *                         theo cột để không phụ thuộc subFormP/subFormS focus.
 * - btnEdit_Click:       split thành Edit-Purchase (P col) / Edit-Sale (S col)
 *                         tương ứng frmMatchingPSMainSubFormEditPlan_p / _s.
 *
 * Permission gate Access: `SCREEN_ACCESS <> 1` → disable toàn bộ buttons.
 * Frontend dùng `usePagePermission().canEdit`.
 */
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  FILTER_HEADER_INPUT_SX,
  FILTER_HEADER_SELECT_SX,
  HIDE_ON_MOBILE_INLINE,
} from '../../../theme/responsive'

import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'

import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import { usePagePermission } from '../../../hooks/usePagePermission'
import {
  useGetMatchCategoriesQuery,
  useGetMatchPOverviewQuery,
  useGetMatchSOverviewQuery,
} from '../../../store/api/matchingPsApi'
import { useMatchingFilterPref, type MatchingFilterCtx } from './matchingFilterPrefs'
import DataPlanDialog from './DataPlanDialog'
import DeletePlanDialog from './DeletePlanDialog'
import DirectPSDialog from './DirectPSDialog'
import EditPurchasePlanDialog from './EditPurchasePlanDialog'
import EditSalePlanDialog from './EditSalePlanDialog'
import StockPInstructionDialog from './StockPInstructionDialog'
import StockSInstructionDialog from './StockSInstructionDialog'

import CircularProgress from '@mui/material/CircularProgress'
import { titleCaseName } from '../../../utils/text'

type Side = 'P' | 'S'
type DateRange = { from: string; to: string }

// Mỗi side gắn 1 màu accent: P = primary (blue), S = secondary (amber/orange).
const ACCENT = {
  P: '#1e88e5',
  S: '#fb8c00',
} as const

type ColumnKind = 'text' | 'date' | 'number' | 'boolean' | 'pending'

interface ColumnDef {
  /** Field key trong JSON response (camelCase, đã được `_row_to_item` serializer chuyển). */
  key: string
  /** Caption Access. */
  label: string
  align?: 'left' | 'right'
  /** Chiều rộng cố định cho cột — vừa với value max (số) hoặc max content (text).
      Cột text dài hơn sẽ ellipsis truncate (xem `ellipsis` flag). */
  width: number
  /** Cố định cột bên trái khi scroll ngang (chỉ áp dụng cho ContractNo). */
  sticky?: boolean
  /** Kiểu cell để chọn formatter. */
  kind?: ColumnKind
  /** Text dài hơn width → ellipsis (Supplier/Buyer/Place). */
  ellipsis?: boolean
  /** Title-case the cell value (proper names stored UPPERCASE in DB). Display-only. */
  proper?: boolean
  /** Tooltip hướng dẫn hiện khi hover header cột. */
  headerTip?: string
}

// Columns mirror Access form `frmMatchingPSQryContractPOverview`.
// Keys = camelCase JSON từ backend (`_row_to_item`).
// Caption + Format chuẩn Access: Format="Standard", DecimalPlaces=0 cho tất cả số;
// Format="DD-MMM-YYYY" cho date. p_plan_pending có ConditionalFormat → highlight > 0.
// Thứ tự cột theo yêu cầu user — không trùng với thứ tự control trong form Access
// (Access bố trí khác để layout, FE re-order để thuận user reading flow).
// Width tính theo max value/content thực tế:
// - Số: "60,000" 6 chars ≈ 48px; "1,200,000" 9 chars ≈ 68px → numeric cells 50-72px
// - Date DD-MMM-YYYY = 11 chars ≈ 84px
// - Code ngắn (Quality, Grade): ≤ 8 chars → 56-64px
// - ContractNo: format mã 10-12 chars → 80px
// - Supplier/Buyer/Place: text dài → fix 120px + ellipsis truncate
const P_COLUMNS: ColumnDef[] = [
  { key: 'contractNo', label: 'ContractNo', width: 80, sticky: true },
  { key: 'cpName', label: 'Supplier', width: 120, ellipsis: true, proper: true },
  { key: 'latestDelivery', label: 'LatestDelivery', width: 84, kind: 'date' },
  {
    key: 'pPlanPending',
    label: 'Pending',
    align: 'right',
    width: 72,
    kind: 'pending',
    headerTip:
      'Purchase quantity not yet planned. Red when ≠ 0 (> 0 still to match; < 0 over-matched).',
  },
  {
    key: 'pDirectS',
    label: 'To S',
    align: 'right',
    width: 68,
    kind: 'number',
    headerTip:
      'Direct P-S quantity to a Sale. Red = planned but DO not fully booked yet (booked DO ≠ planned). Add DO to clear.',
  },
  { key: 'pInstructBaoloc', label: 'To BL', align: 'right', width: 68, kind: 'number' },
  { key: 'pInstructNhontrach', label: 'To NT', align: 'right', width: 68, kind: 'number' },
  { key: 'qualityCode', label: 'Quality', width: 56 },
  { key: 'deliveryPlace', label: 'Place', width: 100, ellipsis: true },
  { key: 'category', label: 'Grade', width: 64 },
  { key: 'executedDirect', label: 'To S (executed)', align: 'right', width: 84, kind: 'number' },
  { key: 'executedBaoloc', label: 'To BL (executed)', align: 'right', width: 84, kind: 'number' },
  { key: 'executedNhontrach', label: 'To NT (executed)', align: 'right', width: 84, kind: 'number' },
  { key: 'isForcedClose', label: 'IsClosed?', width: 64, kind: 'boolean' },
  { key: 'cpCode', label: 'SupplierCode', width: 80 },
  { key: 'contractDate', label: 'ContractDate', width: 84, kind: 'date' },
  { key: 'quantityKg', label: 'QuantityKg', align: 'right', width: 72, kind: 'number' },
  { key: 'npeRef', label: 'NPEref', width: 80, ellipsis: true },
]

// Columns mirror Access form `frmMatchingPSQryContractSOverview`.
// Tương tự P, thứ tự user — bao gồm Pending có conditional highlight > 0.
// S spec không có cột NPEref.
const S_COLUMNS: ColumnDef[] = [
  { key: 'contractNo', label: 'ContractNo', width: 80, sticky: true },
  { key: 'cpName', label: 'Buyer', width: 120, ellipsis: true, proper: true },
  { key: 'deliveryFrom', label: 'ShipmentFrom', width: 84, kind: 'date' },
  {
    key: 'sPlanPending',
    label: 'Pending',
    align: 'right',
    width: 72,
    kind: 'pending',
    headerTip:
      'Sale quantity not yet planned. Red when ≠ 0 (> 0 still to match; < 0 over-matched).',
  },
  {
    key: 'sDirectP',
    label: 'FromP',
    align: 'right',
    width: 68,
    kind: 'number',
    headerTip:
      'Direct P-S from a Purchase. Red = planned but DO not fully booked (booked ≠ planned). Add DO to clear.',
  },
  {
    key: 'sInstructBaoloc',
    label: 'FromBL',
    align: 'right',
    width: 68,
    kind: 'number',
    headerTip:
      'S-Instruction to Bảo Lộc. Red = planned but DO not fully booked yet.',
  },
  {
    key: 'sInstructNhontrach',
    label: 'FromNT',
    align: 'right',
    width: 68,
    kind: 'number',
    headerTip:
      'S-Instruction to Nhơn Trạch. Red = planned but DO not fully booked yet.',
  },
  { key: 'qualityCode', label: 'Quality', width: 56 },
  { key: 'deliveryPlace', label: 'Place', width: 100, ellipsis: true },
  { key: 'category', label: 'Grade', width: 64 },
  { key: 'executedDirect', label: 'FromP (executed)', align: 'right', width: 88, kind: 'number' },
  { key: 'executedBaoloc', label: 'FromBL (executed)', align: 'right', width: 88, kind: 'number' },
  { key: 'executedNhontrach', label: 'FromNT (executed)', align: 'right', width: 88, kind: 'number' },
  { key: 'isFinalized', label: 'IsClosed?', width: 64, kind: 'boolean' },
  { key: 'cpCode', label: 'BuyerCode', width: 80 },
  { key: 'contractDate', label: 'ContractDate', width: 84, kind: 'date' },
  { key: 'quantityKg', label: 'QuantityKg', align: 'right', width: 72, kind: 'number' },
]

// Format mirror Access:
// - DD-MMM-YYYY (date)
// - Standard "#,##0.00;(#,##0.00)" (số, decimals 2, negative dấu ngoặc đơn)
// - text → trim
// - bool "0"/"1" → "" / "Closed" cho IsClosed?
const ACCESS_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const

function formatAccessDate(value: unknown): string {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = d.getFullYear()
  return `${day}-${month}-${year}`
}

/**
 * Format "Standard" của Access — tất cả cột số trong P/S overview spec có
 * DecimalPlaces=0 → integer thousand-sep, không hiển thị phần thập phân.
 * Negative dùng dấu ngoặc đơn theo convention Access ("#,##0;(#,##0)").
 */
function formatAccessNumber(value: unknown): string {
  if (value == null || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  const txt = Math.abs(Math.round(n)).toLocaleString('en-US')
  return n < 0 ? `(${txt})` : txt
}

/** Pending value → numeric (NaN nếu trống); dùng cho ConditionalFormat highlight. */
function pendingNumeric(value: unknown): number {
  if (value == null || value === '') return Number.NaN
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : Number.NaN
}

function formatBooleanLike(value: unknown): string {
  if (value == null || value === '') return ''
  const s = String(value).trim().toLowerCase()
  if (s === '1' || s === 'true' || s === '-1' || s === 'yes' || s === 'closed') return 'Closed'
  if (s === '0' || s === 'false' || s === 'no') return ''
  return String(value)
}

function formatCell(row: Record<string, unknown>, col: ColumnDef): string {
  const value = row[col.key]
  if (col.kind === 'date') return formatAccessDate(value)
  if (col.kind === 'number' || col.kind === 'pending') return formatAccessNumber(value)
  if (col.kind === 'boolean') return formatBooleanLike(value)
  if (value == null || value === '') return ''
  if (col.proper) return titleCaseName(value)
  return String(value)
}

/**
 * Mặc định fiscal year Oct→Sep (theo VBA Form_Load):
 *   - Nếu hôm nay >= 1/10 → [1/10 năm nay, 30/9 năm sau].
 *   - Nếu hôm nay < 1/10 → [1/10 năm trước, 30/9 năm nay].
 */
function defaultFiscalRange(now: Date = new Date()): DateRange {
  const y = now.getFullYear()
  const inQ4OrLater = now.getMonth() >= 9
  const startYear = inQ4OrLater ? y : y - 1
  const endYear = inQ4OrLater ? y + 1 : y
  const iso = (year: number, month: number, day: number) =>
    `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
  return { from: iso(startYear, 10, 1), to: iso(endYear, 9, 30) }
}

const colBtnSx = {
  minWidth: 0,
  px: 1.1,
  py: 0.3,
  minHeight: 28,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none' as const,
  fontWeight: 600,
  '& .MuiButton-startIcon': { mr: 0.5 },
  '& .MuiButton-startIcon > svg': { fontSize: 16 },
}

interface SideColumnProps {
  side: Side
  title: string
  dateLabel: string
  category: string
  onCategoryChange: (v: string) => void
  range: DateRange
  onRangeChange: (next: DateRange) => void
  categoryOptions: string[]
  columns: ColumnDef[]
  /** Rows từ backend (key camelCase). */
  rows: Record<string, unknown>[]
  total: number
  isLoading: boolean
  isFetching: boolean
  /** Side-only actions — handlers (snack-stub khi chưa nối API). */
  onInstruction: () => void
  onEdit: () => void
  onDelete: () => void
  canEdit: boolean
  /** Row hiện được chọn (contract_no). null = không có row nào active.
      Click vào row → emit `onSelectRow` để parent update; tô màu accent + bold. */
  selectedContractNo: string | null
  onSelectRow: (row: Record<string, unknown>) => void
  /** Disable Edit button khi chưa có row nào selected. */
  editEnabled: boolean
}

/**
 * DataRow — 1 row trong bảng overview. Tách ra component riêng + React.memo
 * để khi `selectedContractNo` đổi chỉ 2 row re-render (row vừa unselect + row
 * mới select), 498 row còn lại skip render → click row gần như tức thì.
 *
 * Props phải toàn ref-stable từ parent SideColumn (rows ref ổn định nhờ
 * useMemo, columns module-const, colWidths state object, callbacks memoized).
 */
interface DataRowProps {
  row: Record<string, unknown>
  columns: ColumnDef[]
  colWidths: Record<string, number>
  isSelected: boolean
  accent: string
  onSelectRow: (row: Record<string, unknown>) => void
}

const DataRow = memo(function DataRow({
  row,
  columns,
  colWidths,
  isSelected,
  accent,
  onSelectRow,
}: DataRowProps) {
  return (
    <TableRow
      hover
      onClick={() => onSelectRow(row)}
      sx={{
        cursor: 'pointer',
        ...(isSelected && {
          '& .MuiTableCell-root': {
            bgcolor: `${alpha(accent, 0.18)} !important`,
            fontWeight: 700,
          },
        }),
      }}
    >
      {columns.map((col) => {
        const w = colWidths[col.key] ?? col.width
        // ConditionalFormat Access trên `*_plan_pending`: highlight đỏ khi pending ≠ 0
        // (> 0 còn hàng chờ match; < 0 over-match — đều cần chú ý). Bỏ qua ô trống (NaN).
        const pendingVal = col.kind === 'pending' ? pendingNumeric(row[col.key]) : NaN
        const isPendingHighlight = Number.isFinite(pendingVal) && pendingVal !== 0
        // Các cột block-quantity (To S phía Purchase; FromP/FromBL/FromNT phía Sale):
        // bôi đỏ như Pending khi planned <> 0 và *_do <> planned → chưa Add DO (đủ).
        const NO_DO_COLUMNS: Record<string, string> = {
          pDirectS: 'pDirectSDo',
          sDirectP: 'sDirectPDo',
          sInstructBaoloc: 'sInstructBaolocDo',
          sInstructNhontrach: 'sInstructNhontrachDo',
        }
        let isPlanNoDo = false
        const doKey = NO_DO_COLUMNS[col.key]
        if (doKey) {
          const plan = pendingNumeric(row[col.key])
          const doneRaw = pendingNumeric(row[doKey])
          const done = Number.isFinite(doneRaw) ? doneRaw : 0
          isPlanNoDo = Number.isFinite(plan) && plan !== 0 && done !== plan
        }
        const highlightRed = isPendingHighlight || isPlanNoDo
        const cellText = formatCell(row, col)
        return (
          <TableCell
            key={col.key}
            align={col.align ?? 'left'}
            title={col.ellipsis ? cellText : undefined}
            sx={{
              width: w,
              minWidth: w,
              maxWidth: w,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              ...(highlightRed && {
                bgcolor: alpha('#ed1c24', 0.12),
                color: '#c62828',
                fontWeight: 700,
              }),
              ...(col.sticky && {
                position: 'sticky',
                left: 0,
                zIndex: 1,
                bgcolor: 'background.paper',
                borderLeft: `2px solid ${alpha(accent, 0.6)}`,
                fontWeight: 600,
              }),
            }}
          >
            {cellText}
          </TableCell>
        )
      })}
    </TableRow>
  )
})

const SideColumn = memo(function SideColumn({
  side,
  title,
  dateLabel,
  category,
  onCategoryChange,
  range,
  onRangeChange,
  categoryOptions,
  columns,
  rows,
  total,
  isLoading,
  isFetching,
  onInstruction,
  onEdit,
  onDelete,
  canEdit,
  selectedContractNo,
  onSelectRow,
  editEnabled,
}: SideColumnProps) {
  const accent = ACCENT[side]
  const instructionIcon = side === 'P' ? <ShoppingCartOutlinedIcon /> : <StorefrontOutlinedIcon />
  const instructionLabel = 'Instruct'
  const editLabel = 'Edit'
  const deleteLabel = 'Delete Plan'

  // Resizable column widths — state per side, init từ col.width defaults.
  // User drag grip cạnh phải header → cập nhật state, clamp min 40px.
  // In-memory only (không persist) — refresh page revert về defaults.
  const [colWidths, setColWidths] = useState<Record<string, number>>(() =>
    Object.fromEntries(columns.map((c) => [c.key, c.width])),
  )
  const widthOf = (col: ColumnDef): number => colWidths[col.key] ?? col.width
  const startResize = (e: React.MouseEvent, key: string) => {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startWidth = colWidths[key] ?? columns.find((c) => c.key === key)?.width ?? 80
    const onMove = (mv: MouseEvent) => {
      const next = Math.max(40, startWidth + (mv.clientX - startX))
      setColWidths((prev) => ({ ...prev, [key]: next }))
    }
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    // eslint-disable-next-line react-hooks/immutability -- DOM side-effect, not React state
    document.body.style.cursor = 'col-resize'
    // eslint-disable-next-line react-hooks/immutability -- DOM side-effect, not React state
    document.body.style.userSelect = 'none'
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }
  const totalMinWidth = columns.reduce((sum, c) => sum + widthOf(c), 0)

  // Tổng cộng cho mỗi cột number/pending — render ở footer row sticky bottom.
  // Cột không phải số → không có entry trong map → footer cell hiển thị trống.
  const columnTotals = useMemo(() => {
    const sums: Record<string, number> = {}
    for (const col of columns) {
      if (col.kind !== 'number' && col.kind !== 'pending') continue
      let s = 0
      let any = false
      for (const row of rows) {
        const n = pendingNumeric(row[col.key])
        if (Number.isFinite(n)) {
          s += n
          any = true
        }
      }
      if (any) sums[col.key] = s
    }
    return sums
  }, [columns, rows])

  return (
    <Paper
      elevation={0}
      // Style chuẩn cho bảng dữ liệu trong app — copy pattern SaleContract:
      // border, radius, header gradient, row hover. Định nghĩa CSS:
      // frontend/src/styles/app-shared.css.
      className="app-dataframe"
      sx={{
        width: '100%',
        minWidth: 0,
        // MatchingContractOverviewPage không dùng PageHeader → overhead nhỏ hơn
        // (Tabs + topBar + filter ≈ 180-200px). Tăng height vs default CSS clamp
        // để bảng lấp đầy phần trống dưới cùng.
        //
        // xs: 2 panel stack vertical → nếu giữ 100dvh-170px như md mỗi panel
        // chiếm full viewport, user phải scroll dài. Dùng clamp nhỏ hơn để
        // mỗi panel chiếm ~50-60dvh → cả 2 panel partial visible khi scroll.
        height: {
          xs: 'clamp(360px, calc(60dvh - 20px), 600px)',
          md: 'clamp(520px, calc(100dvh - 200px), 1700px)',
        },
        // Override border-left từ CSS class để giữ accent marker riêng cho P/S.
        borderLeftWidth: 2,
        borderLeftColor: alpha(accent, 0.55),
        borderLeftStyle: 'solid',
      }}
    >
      {/* Banner + toolbar trên 1 hàng — title trái, action buttons dồn phải để
          tiết kiệm height. flexWrap để buttons wrap xuống dòng khi panel quá hẹp,
          không bao giờ tràn sang side kia. */}
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
        sx={{
          pl: 1.5,
          pr: 1,
          py: 0.35,
          minWidth: 0,
          borderBottom: '1px solid',
          borderColor: (t) => alpha(t.palette.text.primary, 0.08),
          bgcolor: (t) => alpha(accent, t.palette.mode === 'dark' ? 0.18 : 0.1),
        }}
      >
        <Chip
          label={side}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.65rem',
            fontWeight: 700,
            letterSpacing: 0.4,
            bgcolor: accent,
            color: '#fff',
            flexShrink: 0,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, fontSize: '0.78rem', minWidth: 0, flex: '1 1 auto' }}
        >
          {title}
          <Typography
            component="span"
            variant="caption"
            sx={{ ml: 1, fontWeight: 500, color: 'text.secondary', fontSize: '0.7rem' }}
          >
            ({total.toLocaleString('en-US')} rows)
          </Typography>
          {isFetching && !isLoading ? (
            <CircularProgress size={11} sx={{ ml: 0.75, color: accent, verticalAlign: 'middle' }} />
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={instructionIcon}
            sx={{
              ...colBtnSx,
              borderColor: alpha(accent, 0.5),
              color: accent,
              bgcolor: 'background.paper',
              '&:hover': { borderColor: accent, bgcolor: alpha(accent, 0.04) },
            }}
            disabled={!canEdit}
            onClick={onInstruction}
            title={instructionLabel}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>{instructionLabel}</Box>
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<EditOutlinedIcon />}
            sx={{
              ...colBtnSx,
              bgcolor: accent,
              color: '#fff',
              '&:hover': { bgcolor: alpha(accent, 0.85) },
              '&.Mui-disabled': { bgcolor: alpha(accent, 0.3), color: '#fff' },
            }}
            disabled={!canEdit || !editEnabled}
            onClick={onEdit}
            title={editEnabled ? editLabel : 'Select a row first'}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>{editLabel}</Box>
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteOutlineIcon />}
            sx={{ ...colBtnSx, bgcolor: 'background.paper' }}
            disabled={!canEdit || !editEnabled}
            onClick={onDelete}
            title={editEnabled ? deleteLabel : 'Select a row first'}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>{deleteLabel}</Box>
          </Button>
        </Stack>
      </Stack>

      {/* Filter — 3 control (Grade + start_date + end_date) BUỘC 1 dòng
          (flexWrap nowrap). Skill /responsive-style Pattern 11: dùng
          FILTER_HEADER_SELECT_SX / FILTER_HEADER_INPUT_SX + label inside; arrow
          icon phân biệt from → to; DatePicker thứ 2 KHÔNG label.
          Để fit ngay cả khi panel hẹp (xs 1 panel = full viewport ~360px;
          md 2 panel = ~430px mỗi panel): Grade width cố định nhỏ + 2
          DatePicker dùng flex grow share phần còn lại + openPickerButton
          shrunk (p:0, icon nhỏ) để text DD-MMM-YYYY luôn đủ chỗ. */}
      <Stack
        direction="row"
        spacing={{ xs: 0.25, sm: 0.5 }}
        alignItems="center"
        useFlexGap
        flexWrap="nowrap"
        sx={{
          px: { xs: 0.5, sm: 1.25 },
          // pt lớn hơn pb: chừa chỗ cho floating label ('Grade'/'Latest Delivery'/
          // 'Shipment Period') nổi lên trên input, tránh bị header band phía trên che.
          pt: { xs: 1, sm: 1.25 },
          pb: { xs: 0.4, sm: 0.5 },
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <TextField
          select
          size="small"
          label="Grade"
          value={category}
          onChange={(e) => onCategoryChange(e.target.value)}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
          sx={{
            ...FILTER_HEADER_SELECT_SX,
            width: { xs: 72, sm: 110, md: 130 },
            flexShrink: 0,
          }}
        >
          <MenuItem value="">
            <em>(all)</em>
          </MenuItem>
          {/* Giữ value persisted (vd "Others") khớp 1 MenuItem kể cả khi options
              chưa load / không chứa nó → tránh MUI out-of-range warning. */}
          {category && !categoryOptions.includes(category) && (
            <MenuItem value={category} sx={{ fontSize: '0.78rem' }}>
              {category}
            </MenuItem>
          )}
          {categoryOptions.map((opt) => (
            <MenuItem key={opt} value={opt} sx={{ fontSize: '0.78rem' }}>
              {opt}
            </MenuItem>
          ))}
        </TextField>
        <DatePicker
          label={dateLabel}
          format="DD-MMM-YYYY"
          value={range.from ? dayjs(range.from) : null}
          onChange={(d) =>
            onRangeChange({ ...range, from: d && d.isValid() ? d.format('YYYY-MM-DD') : '' })
          }
          slotProps={{
            textField: {
              size: 'small',
              InputLabelProps: { shrink: true },
              sx: {
                ...FILTER_HEADER_INPUT_SX,
                width: 'auto',
                flex: '1 1 110px',
                minWidth: 108,
              },
              inputProps: { 'aria-label': `${dateLabel} start date` },
            },
            openPickerButton: { sx: { p: { xs: 0, sm: 0.25 } } },
            openPickerIcon: { sx: { fontSize: { xs: 13, sm: 16 } } },
          }}
        />
        <ArrowRightAltIcon
          sx={{
            fontSize: { xs: 14, sm: 18 },
            color: 'text.disabled',
            flexShrink: 0,
            mx: { xs: 0.1, sm: 0.25 },
          }}
          aria-hidden="true"
        />
        <DatePicker
          format="DD-MMM-YYYY"
          value={range.to ? dayjs(range.to) : null}
          onChange={(d) =>
            onRangeChange({ ...range, to: d && d.isValid() ? d.format('YYYY-MM-DD') : '' })
          }
          slotProps={{
            textField: {
              size: 'small',
              sx: {
                ...FILTER_HEADER_INPUT_SX,
                width: 'auto',
                flex: '1 1 110px',
                minWidth: 108,
              },
              inputProps: { 'aria-label': `${dateLabel} end date` },
            },
            openPickerButton: { sx: { p: { xs: 0, sm: 0.25 } } },
            openPickerIcon: { sx: { fontSize: { xs: 13, sm: 16 } } },
          }}
        />
      </Stack>

      <Divider sx={{ opacity: 0.6 }} />

      {/* Loading / empty placeholder — render OUTSIDE Table để center theo width
          của panel (50% viewport) thay vì width của bảng (totalMinWidth >> 50%
          khi cột tổng wider hơn panel, khiến text lệch sang phải khi scroll). */}
      {isLoading || rows.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#ffffff',
          }}
        >
          <Stack spacing={0.75} alignItems="center">
            {isLoading ? (
              <>
                <CircularProgress size={18} sx={{ color: accent }} />
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                  Loading data…
                </Typography>
              </>
            ) : (
              <>
                <InboxOutlinedIcon sx={{ fontSize: 34, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.72rem' }}>
                  No data for the selected date range / grade.
                </Typography>
              </>
            )}
          </Stack>
        </Box>
      ) : (
        /* Table — `app-table-wrap` lo flex:1 + overflow:auto, app-shared.css
           handle header gradient + hover. Override: (1) bỏ alternate-row tint —
           CSS shared dùng selector 4-class deep, phải `!important` mới beat;
           (2) thu nhỏ font body 0.68rem / header 0.7rem để bảng nén thông tin
           chặt hơn.
           Reload/refetch (fetching && !loading): LinearProgress 2px + bảng mờ
           opacity 0.55 + khóa tương tác — GIỮ rows cũ (đồng bộ DataframeBody của
           2 tab S/P Contract) để hiệu ứng reload thấy rõ mà không nháy trắng. */
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {isFetching && !isLoading && <LinearProgress sx={{ height: 2, borderRadius: 1 }} />}
          <Box
            className="app-table-wrap"
            sx={{
              opacity: isFetching && !isLoading ? 0.55 : 1,
              pointerEvents: isFetching && !isLoading ? 'none' : 'auto',
              transition: 'opacity 0.15s',
            }}
          >
          <Table
            size="small"
            stickyHeader
            sx={{
              minWidth: totalMinWidth,
              tableLayout: 'fixed', // bắt buộc fixed để width đặt cho col được respect
              // Override `.app-table-wrap .MuiTable-root { min-height: 100% }` (app-shared.css):
              // khi ít dòng, min-height:100% kéo table cao bằng wrapper → trình duyệt
              // chia đều height dư cho các <tr> → dòng bị giãn cao. Ép min-height:0 để
              // table cao tự nhiên theo nội dung, mỗi dòng giữ height mặc định, phần
              // trống dồn xuống dưới. Chỉ scope cho bảng P/S Main này.
              minHeight: '0 !important',
              '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)': {
                background: 'transparent !important',
              },
              '& .MuiTableBody-root .MuiTableCell-root': {
                fontSize: '0.68rem',
                py: 0.35,
                px: 0.75,
              },
              '& .MuiTableHead-root .MuiTableCell-root, & .MuiTableCell-head': {
                fontSize: '0.7rem',
                py: 0.4,
                px: 0.5,
              },
            }}
          >
            <TableHead>
              <TableRow>
                {columns.map((col) => {
                  const w = widthOf(col)
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align ?? 'left'}
                      className="app-table-header-cell"
                      // Inline style toàn bộ sticky overrides — sx không thắng được:
                      // (1) backgroundColor/Image: CSS `app-shared.css:119` selector
                      //     4-class deep (0,0,4,0) set `background:` rgba; sx single-class
                      //     thua → sticky cell bị semi-transparent → bleed-through.
                      // (2) zIndex: MUI `.MuiTableCell-stickyHeader` đặt z-index:2;
                      //     sx zIndex:6 không thắng được do specificity tie + style
                      //     insertion order → header "Supplier" che "ContractNo" khi
                      //     scroll ngang. Inline style (specificity 1,0,0,0) thắng tất.
                      style={
                        col.sticky
                          ? {
                              position: 'sticky',
                              left: 0,
                              zIndex: 6,
                              backgroundColor: '#fff9f4',
                              backgroundImage:
                                'linear-gradient(180deg, #fff9f4 0%, #f4df9c 100%)',
                              borderLeft: `2px solid ${alpha(accent, 0.6)}`,
                            }
                          : undefined
                      }
                      sx={{
                        width: w,
                        minWidth: w,
                        maxWidth: w,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        // position:relative để resize grip (absolute) anchor.
                        // Sticky col đặt qua inline style ở trên (sticky cũng tạo
                        // containing block cho absolute children, OK cho grip).
                        ...(col.sticky ? {} : { position: 'relative' }),
                      }}
                    >
                      {col.headerTip ? (
                        <Tooltip title={col.headerTip} arrow placement="top">
                          <Box
                            component="span"
                            sx={{ textDecoration: 'underline dotted', cursor: 'help' }}
                          >
                            {col.label}
                          </Box>
                        </Tooltip>
                      ) : (
                        col.label
                      )}
                      {/* Resize grip — drag để chỉnh width cột. Hover/active hiện
                          accent strip giúp user thấy được vị trí grip. */}
                      <Box
                        onMouseDown={(e) => startResize(e, col.key)}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          width: 6,
                          height: '100%',
                          cursor: 'col-resize',
                          userSelect: 'none',
                          zIndex: 7,
                          '&:hover': { bgcolor: alpha(accent, 0.5) },
                          '&:active': { bgcolor: accent },
                        }}
                      />
                    </TableCell>
                  )
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => {
                const contractNo = String(row.contractNo ?? '')
                const rowKey = `${contractNo}-${idx}`
                const isSelected =
                  selectedContractNo !== null && contractNo === selectedContractNo
                return (
                  <DataRow
                    key={rowKey}
                    row={row}
                    columns={columns}
                    colWidths={colWidths}
                    isSelected={isSelected}
                    accent={accent}
                    onSelectRow={onSelectRow}
                  />
                )
              })}
                {/* TOTAL footer — sticky bottom, sum tất cả cột số (number/pending).
                    Cột sticky ContractNo hiện label "TOTAL" để user nhận diện hàng.
                    BG phải opaque (không alpha) — sticky cell nằm ở vị trí cố định,
                    data rows scroll phía sau sẽ "xuyên qua" nếu bg semi-transparent. */}
                <TableRow>
                  {columns.map((col, colIdx) => {
                    const total = columnTotals[col.key]
                    const isFirst = colIdx === 0
                    const text = total !== undefined
                      ? formatAccessNumber(total)
                      : isFirst
                        ? 'TOTAL'
                        : ''
                    const w = widthOf(col)
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align ?? 'left'}
                        sx={{
                          width: w,
                          minWidth: w,
                          maxWidth: w,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          position: 'sticky',
                          bottom: 0,
                          // sticky-left + sticky-bottom intersection cần z-index cao nhất
                          zIndex: col.sticky ? 4 : 3,
                          // Solid bg để không thấy data rows phía sau khi scroll dọc.
                          backgroundColor: (t) =>
                            t.palette.mode === 'dark' ? '#243042' : '#eef1f5',
                          fontWeight: 800,
                          borderTop: (t) => `2px solid ${alpha(t.palette.text.primary, 0.25)}`,
                          color: 'text.primary',
                          ...(col.sticky && {
                            left: 0,
                            borderLeft: `2px solid ${alpha(accent, 0.6)}`,
                            letterSpacing: 0.4,
                          }),
                        }}
                      >
                        {text}
                      </TableCell>
                    )
                  })}
                </TableRow>
            </TableBody>
          </Table>
          </Box>
        </Box>
      )}
    </Paper>
  )
})

export default function MatchInstructTab() {
  const { canEdit } = usePagePermission()

  // Filter state độc lập cho từng side (Access lưu vào public.tmp_filter_date theo
  // module match_p / match_s; webapp pass thẳng qua query param thay vì persist).
  const fiscalDefault = useMemo(() => defaultFiscalRange(), [])
  const [categoryP, setCategoryP] = useState('')
  const [rangeP, setRangeP] = useState<DateRange>(fiscalDefault)
  const [categoryS, setCategoryS] = useState('')
  const [rangeS, setRangeS] = useState<DateRange>(fiscalDefault)

  // Persist filter page-level qua user_preferences (skill /user-logaction).
  // 2 context: main-p / main-s. Patch per-field khi user đổi control; query
  // chỉ chạy sau khi hydrate xong (gate `hydrated`) → tránh fetch thừa với
  // default trước khi prefs về. Per-column ▽ filter trong table KHÔNG persist.
  const pFilterPref = useMatchingFilterPref<MatchingFilterCtx>('main-p')
  const sFilterPref = useMatchingFilterPref<MatchingFilterCtx>('main-s')
  const { patch: patchPFilter } = pFilterPref
  const { patch: patchSFilter } = sFilterPref
  // Ref-latch để hydrate đúng 1 lần (đồng pattern PurchaseContractPage) — set ref
  // trước khi setState nên không trip rule react-hooks/set-state-in-effect.
  const hydratedRef = useRef(false)
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    if (hydratedRef.current || pFilterPref.isLoading || sFilterPref.isLoading) return
    hydratedRef.current = true
    const pv = pFilterPref.value
    const sv = sFilterPref.value
    setCategoryP(pv.category ?? '')
    setRangeP({ from: pv.dateFrom || fiscalDefault.from, to: pv.dateTo || fiscalDefault.to })
    setCategoryS(sv.category ?? '')
    setRangeS({ from: sv.dateFrom || fiscalDefault.from, to: sv.dateTo || fiscalDefault.to })
    setHydrated(true)
  }, [pFilterPref, sFilterPref, fiscalDefault])

  // Sync ngược prefs → local khi context bị form KHÁC dùng chung ghi đè (vd
  // popup P-Instruction share `main-p`). patchPreference cập nhật cache
  // optimistic + đồng bộ nên `value` đổi ngay khi popup patch → block Purchase
  // ngoài refresh tức thì. Guard so sánh field (value identity stable qua
  // useMemo) → không loop; local-edit tự patch cùng giá trị nên idempotent.
  useEffect(() => {
    if (!hydrated) return
    const pv = pFilterPref.value
    const pCat = pv.category ?? ''
    const pFrom = pv.dateFrom || fiscalDefault.from
    const pTo = pv.dateTo || fiscalDefault.to
    const sv = sFilterPref.value
    const sCat = sv.category ?? ''
    const sFrom = sv.dateFrom || fiscalDefault.from
    const sTo = sv.dateTo || fiscalDefault.to
    /* eslint-disable react-hooks/set-state-in-effect -- sync prefs→local (field-guarded, no loop) */
    if (pCat !== categoryP) setCategoryP(pCat)
    if (pFrom !== rangeP.from || pTo !== rangeP.to) setRangeP({ from: pFrom, to: pTo })
    if (sCat !== categoryS) setCategoryS(sCat)
    if (sFrom !== rangeS.from || sTo !== rangeS.to) setRangeS({ from: sFrom, to: sTo })
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [
    hydrated,
    pFilterPref.value,
    sFilterPref.value,
    fiscalDefault,
    categoryP,
    rangeP.from,
    rangeP.to,
    categoryS,
    rangeS.from,
    rangeS.to,
  ])

  // Wrapped handlers — set local state (drive query args) + persist delta.
  // patch* là callback ref-stable (usePreference) → không bust memo của SideColumn.
  const handleCategoryP = useCallback(
    (v: string) => {
      setCategoryP(v)
      void patchPFilter({ category: v })
    },
    [patchPFilter],
  )
  const handleRangeP = useCallback(
    (next: DateRange) => {
      setRangeP(next)
      void patchPFilter({ dateFrom: next.from || null, dateTo: next.to || null })
    },
    [patchPFilter],
  )
  const handleCategoryS = useCallback(
    (v: string) => {
      setCategoryS(v)
      void patchSFilter({ category: v })
    },
    [patchSFilter],
  )
  const handleRangeS = useCallback(
    (next: DateRange) => {
      setRangeS(next)
      void patchSFilter({ dateFrom: next.from || null, dateTo: next.to || null })
    },
    [patchSFilter],
  )

  // Row selection state — click row trên P/S table để chọn. Edit button dùng
  // row đang selected (mirror Access — Me. trỏ row đang focus trong subform).
  const [selectedP, setSelectedP] = useState<Record<string, unknown> | null>(null)
  const [selectedS, setSelectedS] = useState<Record<string, unknown> | null>(null)
  const [editPurchaseOpen, setEditPurchaseOpen] = useState(false)
  const [editSaleOpen, setEditSaleOpen] = useState(false)
  // Delete dialog state — chung 1 cặp open/side cho cả 2 cột (chỉ mở 1 cái 1
  // lúc). `deleteSide` xác định row context (selectedP hoặc selectedS) khi mở.
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteSide, setDeleteSide] = useState<Side>('P')

  // RowSource Access: SELECT DISTINCT category FROM public.list_and_valo WHERE is_status=0
  const categoryOptionsQuery = useGetMatchCategoriesQuery()
  const categoryOptions = categoryOptionsQuery.data ?? []

  // Fetch P + S overview với filter date/category hiện tại.
  const pQuery = useGetMatchPOverviewQuery(
    {
      startDate: rangeP.from || undefined,
      endDate: rangeP.to || undefined,
      category: categoryP || undefined,
    },
    { skip: !hydrated },
  )
  const sQuery = useGetMatchSOverviewQuery(
    {
      startDate: rangeS.from || undefined,
      endDate: rangeS.to || undefined,
      category: categoryS || undefined,
    },
    { skip: !hydrated },
  )

  // Direct P-S dialog — popup `frmMatchingPSMainSubFormDirect`.
  const [directOpen, setDirectOpen] = useState(false)
  // Data Plan dialog — popup `frmMatchingPSMainSubFormDataPlan` (read-only).
  const [dataPlanOpen, setDataPlanOpen] = useState(false)
  // P - Instruction dialog — popup `frmMatchingPSMainSubFormStockP` (P only).
  const [stockPOpen, setStockPOpen] = useState(false)
  // S - Instruction dialog — popup `frmMatchingPSMainSubFormStockS` (S only).
  const [stockSOpen, setStockSOpen] = useState(false)

  // Reload đã chuyển lên thanh chung cấp trang (MatchingContractOverviewPage) — 1
  // nút Reload cho toàn function, invalidate tag `MatchingPs` → refetch tab đang mở.

  // Memoize callbacks/objects truyền xuống SideColumn — nếu ref đổi mỗi render,
  // React.memo skip-render không kích hoạt và 500 rows × 17 cells phải re-compute
  // sx khi click row → click delay ~vài giây. Click 1 side không re-render side kia.
  const handleSelectP = useCallback((r: Record<string, unknown>) => setSelectedP(r), [])
  const handleSelectS = useCallback((r: Record<string, unknown>) => setSelectedS(r), [])
  const onInstructPurchase = useCallback(() => setStockPOpen(true), [])
  const onInstructSale = useCallback(() => setStockSOpen(true), [])
  const onEditPurchase = useCallback(() => setEditPurchaseOpen(true), [])
  const onEditSale = useCallback(() => setEditSaleOpen(true), [])
  const onDeletePurchase = useCallback(() => {
    setDeleteSide('P')
    setDeleteOpen(true)
  }, [])
  const onDeleteSale = useCallback(() => {
    setDeleteSide('S')
    setDeleteOpen(true)
  }, [])

  // Stable refs cho rows/selected — `pQuery.data?.items ?? []` mỗi render tạo
  // array mới khi data === undefined; useMemo neo tham chiếu ổn định cho memo.
  const pRows = useMemo(() => pQuery.data?.items ?? [], [pQuery.data])
  const sRows = useMemo(() => sQuery.data?.items ?? [], [sQuery.data])
  const selectedPContract = selectedP ? String(selectedP.contractNo ?? '') : null
  const selectedSContract = selectedS ? String(selectedS.contractNo ?? '') : null

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 1, gap: 1 }}>
      {/* Top bar trung lập — cross-side actions (Reload + Direct P↔S + Data Plan). */}
      <Paper
        elevation={0}
        sx={{
          px: 1.25,
          py: 0.6,
          border: (t) => `1px solid ${alpha(t.palette.text.primary, 0.1)}`,
          borderRadius: 1,
          bgcolor: (t) => alpha(t.palette.text.primary, 0.02),
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          {/* <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.72rem', letterSpacing: 0.3 }}
          >
            Match - Instruct
          </Typography> */}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<SwapHorizIcon />}
            sx={colBtnSx}
            onClick={() => setDirectOpen(true)}
            title="Direct P ↔ S"
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Direct P ↔ S</Box>
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            startIcon={<TableChartOutlinedIcon />}
            sx={colBtnSx}
            onClick={() => setDataPlanOpen(true)}
            title="Data Plan"
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Data Plan</Box>
          </Button>
        </Stack>
      </Paper>

      {/* Body — 2 cột độc lập, mỗi cột tự chứa toolbar/filter/table riêng.
          spacing=2 → gap rõ giữa 2 column để mắt phân biệt được P / S là 2 block riêng. */}
      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        <Grid item xs={12} md={6} sx={{ display: 'flex', minWidth: 0 }}>
          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex' }}>
            <SideColumn
              side="P"
              title="Purchase"
              dateLabel="Latest Delivery"
              category={categoryP}
              onCategoryChange={handleCategoryP}
              range={rangeP}
              onRangeChange={handleRangeP}
              categoryOptions={categoryOptions}
              columns={P_COLUMNS}
              rows={pRows}
              total={pQuery.data?.total ?? 0}
              isLoading={pQuery.isLoading || !hydrated}
              isFetching={pQuery.isFetching}
              onInstruction={onInstructPurchase}
              onEdit={onEditPurchase}
              onDelete={onDeletePurchase}
              canEdit={canEdit}
              selectedContractNo={selectedPContract}
              onSelectRow={handleSelectP}
              editEnabled={selectedP !== null}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6} sx={{ display: 'flex', minWidth: 0 }}>
          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex' }}>
            <SideColumn
              side="S"
              title="Sales"
              dateLabel="Shipment Period"
              category={categoryS}
              onCategoryChange={handleCategoryS}
              range={rangeS}
              onRangeChange={handleRangeS}
              categoryOptions={categoryOptions}
              columns={S_COLUMNS}
              rows={sRows}
              total={sQuery.data?.total ?? 0}
              isLoading={sQuery.isLoading || !hydrated}
              isFetching={sQuery.isFetching}
              onInstruction={onInstructSale}
              onEdit={onEditSale}
              onDelete={onDeleteSale}
              canEdit={canEdit}
              selectedContractNo={selectedSContract}
              onSelectRow={handleSelectS}
              editEnabled={selectedS !== null}
            />
          </Box>
        </Grid>
      </Grid>

      <EditPurchasePlanDialog
        open={editPurchaseOpen}
        onClose={() => setEditPurchaseOpen(false)}
        row={selectedP}
        canEdit={canEdit}
      />
      <EditSalePlanDialog
        open={editSaleOpen}
        onClose={() => setEditSaleOpen(false)}
        row={selectedS}
        canEdit={canEdit}
      />
      <DirectPSDialog
        open={directOpen}
        onClose={() => setDirectOpen(false)}
        canEdit={canEdit}
      />
      <DataPlanDialog
        open={dataPlanOpen}
        onClose={() => setDataPlanOpen(false)}
        canEdit={canEdit}
      />
      <DeletePlanDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        side={deleteSide}
        row={deleteSide === 'P' ? selectedP : selectedS}
        canEdit={canEdit}
      />
      <StockPInstructionDialog
        open={stockPOpen}
        onClose={() => setStockPOpen(false)}
        canEdit={canEdit}
      />
      <StockSInstructionDialog
        open={stockSOpen}
        onClose={() => setStockSOpen(false)}
        canEdit={canEdit}
      />
    </Box>
  )
}
